import React, { useState, useEffect } from 'react';
import { Award, Users, Heart, Star, Shield, Check, Send, MessageCircle, ChevronRight, TrendingUp } from 'lucide-react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { apiClient } from '../api/client';

const MentorMatching = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBecome, setShowBecome] = useState(false);
  const [bio, setBio] = useState('');
  const [strengths, setStrengths] = useState([]);
  const [becoming, setBecoming] = useState(false);
  const [checkInNote, setCheckInNote] = useState('');
  const [checkInId, setCheckInId] = useState(null);

  useEffect(() => {
    apiClient.request('/api/self-dev/mentorship')
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleBecomeMentor = async () => {
    setBecoming(true);
    try {
      await apiClient.request('/api/self-dev/mentorship/become-mentor', {
        method: 'POST',
        body: JSON.stringify({ bio, strengths }),
      });
      setShowBecome(false);
      apiClient.request('/api/self-dev/mentorship').then(setData).catch(() => {});
    } catch {}
    setBecoming(false);
  };

  const handleRequestMentor = async (mentorId) => {
    try {
      await apiClient.request(`/api/self-dev/mentorship/request/${mentorId}`, { method: 'POST' });
      apiClient.request('/api/self-dev/mentorship').then(setData).catch(() => {});
    } catch {}
  };

  const handleCheckIn = async () => {
    if (!checkInId || !checkInNote.trim()) return;
    try {
      await apiClient.request(`/api/self-dev/mentorship/${checkInId}/check-in`, {
        method: 'POST',
        body: JSON.stringify({ note: checkInNote.trim() }),
      });
      setCheckInId(null);
      setCheckInNote('');
      apiClient.request('/api/self-dev/mentorship').then(setData).catch(() => {});
    } catch {}
  };

  const toggleStrength = (s) => {
    setStrengths(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  };

  const STRENGTH_OPTIONS = ['Active Listening', 'Confidence', 'Empathy', 'Assertiveness', 'Public Speaking', 'Making Friends', 'Conflict Resolution'];

  if (loading) return <DashboardLayout><div className="p-8 text-center text-gray-400">Loading mentorship...</div></DashboardLayout>;
  if (!data) return <DashboardLayout><div className="p-8 text-center text-gray-400">Could not load mentorship</div></DashboardLayout>;

  const progressPercent = Math.min(100, Math.round((data.confidence_score / data.mentor_threshold) * 100));

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-8 max-w-3xl mx-auto" data-testid="mentor-matching-page">
        <div className="text-center mb-6">
          <h1 className="text-2xl sm:text-3xl font-black text-gray-900 flex items-center justify-center gap-2">
            <Award size={28} className="text-amber-500" />
            Mentor Matching
          </h1>
          <p className="text-sm text-gray-500 mt-1">Learn from Champions, guide Newcomers</p>
        </div>

        {/* Mentor Status */}
        <div className="mb-6 bg-white rounded-2xl border border-gray-200 shadow-sm p-5" data-testid="mentor-status">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-gray-900">Your Mentor Status</h3>
            {data.can_be_mentor ? (
              <span className="px-2 py-1 bg-green-100 text-green-700 text-[10px] font-bold rounded-full">Eligible to Mentor</span>
            ) : (
              <span className="px-2 py-1 bg-gray-100 text-gray-500 text-[10px] font-bold rounded-full">Building Skills</span>
            )}
          </div>
          <div className="mb-2">
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>Confidence Score</span>
              <span>{data.confidence_score}/{data.mentor_threshold}</span>
            </div>
            <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full transition-all" style={{ width: `${progressPercent}%` }} />
            </div>
          </div>
          {data.can_be_mentor && !data.is_mentor && (
            <button onClick={() => setShowBecome(true)} className="mt-3 w-full py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-xl text-sm hover:shadow-lg" data-testid="become-mentor-btn">
              Become a Mentor (+50 hearts)
            </button>
          )}
        </div>

        {/* Become Mentor Form */}
        {showBecome && (
          <div className="mb-6 p-5 bg-amber-50 border-2 border-amber-200 rounded-2xl" data-testid="become-mentor-form">
            <h3 className="font-bold text-gray-900 mb-3">Set Up Your Mentor Profile</h3>
            <textarea value={bio} onChange={e => setBio(e.target.value)} placeholder="Tell mentees about yourself and your journey..." className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-3" rows={3} data-testid="mentor-bio-input" />
            <p className="text-xs font-medium text-gray-700 mb-2">Your Strengths (select up to 3):</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {STRENGTH_OPTIONS.map(s => (
                <button key={s} onClick={() => toggleStrength(s)} className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${strengths.includes(s) ? 'bg-amber-500 text-white' : 'bg-white border border-gray-300 text-gray-600'}`} data-testid={`strength-${s.replace(/\s/g, '-').toLowerCase()}`}>
                  {s}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowBecome(false)} className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm">Cancel</button>
              <button onClick={handleBecomeMentor} disabled={becoming} className="flex-1 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-lg text-sm disabled:opacity-50" data-testid="submit-mentor-btn">{becoming ? 'Setting up...' : 'Launch Mentor Profile'}</button>
            </div>
          </div>
        )}

        {/* Active Mentoring */}
        {data.as_mentor.length > 0 && (
          <div className="mb-6">
            <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">Your Mentees</h2>
            <div className="space-y-2">
              {data.as_mentor.map(ms => (
                <div key={ms.mentorship_id} className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm" data-testid={`mentee-${ms.mentorship_id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-bold text-sm">{ms.mentee_name.charAt(0)}</div>
                      <div>
                        <p className="text-sm font-bold text-gray-900">{ms.mentee_name}</p>
                        <p className="text-[10px] text-gray-400">{ms.check_ins} check-ins</p>
                      </div>
                    </div>
                    <button onClick={() => setCheckInId(ms.mentorship_id)} className="px-3 py-1.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded-lg" data-testid={`mentor-checkin-${ms.mentorship_id}`}>
                      Check In
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* My Mentor */}
        {data.as_mentee && (
          <div className="mb-6">
            <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">My Mentor</h2>
            <div className="p-4 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-xl" data-testid="my-mentor-card">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-amber-400 flex items-center justify-center text-white font-bold text-lg">{data.as_mentee.mentor_name.charAt(0)}</div>
                  <div>
                    <p className="text-base font-bold text-gray-900">{data.as_mentee.mentor_name}</p>
                    <p className="text-xs text-gray-500">{data.as_mentee.check_ins} check-ins together</p>
                  </div>
                </div>
                <button onClick={() => setCheckInId(data.as_mentee.mentorship_id)} className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-semibold rounded-xl" data-testid="mentee-checkin-btn">
                  Check In
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Check-In Modal */}
        {checkInId && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-sm" data-testid="mentorship-checkin-modal">
              <h3 className="font-bold text-gray-900 mb-3 text-center">Mentorship Check-In</h3>
              <textarea value={checkInNote} onChange={e => setCheckInNote(e.target.value)} placeholder="Share progress, ask questions, or just say hi..." className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-3" rows={3} data-testid="mentorship-note-input" />
              <div className="flex gap-2">
                <button onClick={() => setCheckInId(null)} className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm">Cancel</button>
                <button onClick={handleCheckIn} disabled={!checkInNote.trim()} className="flex-1 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-lg text-sm disabled:opacity-50" data-testid="submit-mentorship-checkin">Submit</button>
              </div>
            </div>
          </div>
        )}

        {/* Available Mentors */}
        {!data.has_mentor && data.available_mentors.length > 0 && (
          <div className="mb-6">
            <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">Available Mentors</h2>
            <div className="space-y-3">
              {data.available_mentors.map(m => (
                <div key={m.mentor_id} className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm" data-testid={`mentor-${m.mentor_id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-bold">{m.name.charAt(0)}</div>
                      <div>
                        <p className="text-sm font-bold text-gray-900">{m.name}</p>
                        <p className="text-xs text-gray-500">{m.bio}</p>
                        <div className="flex gap-1 mt-1">
                          {(m.strengths || []).map((s, i) => (
                            <span key={i} className="px-1.5 py-0.5 bg-amber-50 text-amber-700 text-[9px] font-bold rounded">{s}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <button onClick={() => handleRequestMentor(m.mentor_id)} className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-semibold rounded-xl" data-testid={`request-mentor-${m.mentor_id}`}>
                      Request
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!data.has_mentor && data.available_mentors.length === 0 && !data.is_mentor && (
          <div className="text-center py-8 text-gray-500" data-testid="no-mentors-available">
            <Award size={40} className="text-gray-300 mx-auto mb-3" />
            <p className="text-sm">No mentors available yet. {data.can_be_mentor ? 'Be the first to mentor!' : `Reach ${data.mentor_threshold} confidence score to become one.`}</p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MentorMatching;
